import React from 'react';
import { Phone, Mail, MapPin, Instagram, ArrowUp } from 'lucide-react';
import { resetConsent } from './CookieConsent';

const LOGO_URL = "/logo.png";

const services = [
  'Altbauinstallationen',
  'Modernisierungen',
  'Sprechanlagen',
  'SAT- & Empfangsanlagen',
  'Nachtspeicherheizungen',
  'Rauchwarnmelder',
  'E-Check',
  'Allgemeine Elektroarbeiten',
];

const links = [
  { label: 'Leistungen', href: '#leistungen' },
  { label: 'Über uns', href: '#ueber-uns' },
  { label: 'Prozess', href: '#prozess' },
  { label: 'Referenzen', href: '#referenzen' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Kontakt', href: '#kontakt' },
];

export default function Footer({ onShowImpressum, onShowDatenschutz }) {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer data-testid="footer" className="bg-[#1A3A22] text-white relative">
      {/* Back to top */}
      <button
        onClick={scrollToTop}
        data-testid="back-to-top"
        className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-[#2D5A27] hover:bg-[#10B981] flex items-center justify-center transition-colors duration-300"
      >
        <ArrowUp className="w-5 h-5 text-white" />
      </button>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Company */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Daniels GmbH" className="w-12 h-12 rounded-full object-cover" />
              <div>
                <span className="font-['Outfit'] font-bold text-base text-white block">DANIELS</span>
                <span className="font-['Manrope'] text-white/50 text-xs tracking-wider uppercase">Elektroinstallationen</span>
              </div>
            </div>
            <p className="font-['Manrope'] text-white/50 text-sm leading-relaxed mb-4">
              K.H. und B. Daniels Elektroinstallationen GmbH – Ihr familiengeführter Meisterbetrieb
              für professionelle Elektroinstallationen in Bonn seit 1966.
            </p>
            <p className="font-['Manrope'] text-white/40 text-xs leading-relaxed mb-6">
              Mitglied der Innung Köln & Kreishandwerkerschaft Bonn/Rhein-Sieg.
              Auch im Raum Köln vertreten.
            </p>
            <a
              href="https://www.instagram.com/elektro.daniels/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="inline-flex items-center gap-2 text-white/50 hover:text-[#10B981] transition-colors text-sm"
            >
              <Instagram className="w-5 h-5" strokeWidth={1.5} />
              @elektro.daniels
            </a>
          </div>

          {/* Leistungen */}
          <div>
            <h4 className="font-['Outfit'] font-semibold text-base text-white mb-6">Leistungen</h4>
            <ul className="space-y-3">
              {services.map((service, idx) => (
                <li key={idx}>
                  <span className="font-['Manrope'] text-white/50 hover:text-[#10B981] transition-colors text-sm cursor-default">
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-['Outfit'] font-semibold text-base text-white mb-6">Navigation</h4>
            <ul className="space-y-3">
              {links.map((link, idx) => (
                <li key={idx}>
                  <button
                    onClick={() => scrollTo(link.href)}
                    data-testid={`footer-link-${link.href.replace('#', '')}`}
                    className="font-['Manrope'] text-white/50 hover:text-[#10B981] transition-colors text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="font-['Outfit'] font-semibold text-base text-white mb-6">Kontakt</h4>
            <div className="space-y-4">
              <a href="tel:+492282346 44" className="flex items-start gap-3 group" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="font-['Manrope'] text-white/70 text-sm group-hover:text-[#10B981] transition-colors">
                  +49 228 23 46 44
                </span>
              </a>
              <a href="mailto:info@daniels-gmbh.net" className="flex items-center gap-3 group" data-testid="footer-email">
                <Mail className="w-4 h-4 text-[#10B981] flex-shrink-0" strokeWidth={1.5} />
                <span className="font-['Manrope'] text-white/70 text-sm group-hover:text-[#10B981] transition-colors">
                  info@daniels-gmbh.net
                </span>
              </a>
              <div className="flex items-start gap-3" data-testid="footer-address">
                <MapPin className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <span className="font-['Manrope'] text-white/70 text-sm block">Burbacher Straße 177</span>
                  <span className="font-['Manrope'] text-white/70 text-sm">53129 Bonn</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-['Manrope'] text-white/30 text-xs">
            &copy; {new Date().getFullYear()} K.H. und B. Daniels Elektroinstallationen GmbH. Alle Rechte vorbehalten.
          </p>
          <div className="flex gap-6">
            <button onClick={onShowImpressum} data-testid="footer-impressum" className="font-['Manrope'] text-white/30 hover:text-white/60 text-xs transition-colors">
              Impressum
            </button>
            <button onClick={onShowDatenschutz} data-testid="footer-datenschutz" className="font-['Manrope'] text-white/30 hover:text-white/60 text-xs transition-colors">
              Datenschutz
            </button>
            <button onClick={resetConsent} data-testid="footer-cookie-settings" className="font-['Manrope'] text-white/30 hover:text-white/60 text-xs transition-colors">
              Cookie-Einstellungen
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
